import { useState, useEffect, useRef } from 'react';
import { NoRecords, PageLoader } from '../../lib/loader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ArrowDown, ArrowUp, BadgeCheck, BadgeMinus, PlusCircle, Search, Trash, UserRoundPen, View } from 'lucide-react';
import { Modal, ModalBody } from 'reactstrap';
import moment from 'moment';
import useConfirm from '../../hooks/useConfirm';
import { useDebounce } from '../../hooks/debounce';
import { useDeleteSub, useSubAdminHistory, useUpdateSubAdmin } from './api/index';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import user from '../../assets/images/icons/user.svg';
import { useToast } from '../../hooks/usetoast';
import { queryClient } from '../../config';
import PermissionDenied from '../common/permissionDenied';
import { useCheckAdminAccess } from '../WhiteIp/api/whiteipapi';
import { logout } from '../../hooks/logout';
import { WaitingLoader } from '../../hooks/buttonLoad';
import { useBreadcrumbPagination } from '../../hooks/useBreadcrumbPagination';
import BreadcrumbPagination from '../../utils/BreadcrumbPagination';

const allOptions = [
    { module: "Login History", link: "/login-history", icon: "clock-outline" },
    { module: "User Management", link: "/user-management", icon: "clock-outline" },
    { module: "Homepage Management", link: "", icon: "block-outline" },
    { module: "Send Management", link: "", icon: "clock-outline" },
    { module: "Receive Management", link: "", icon: "clock-outline" },
    { module: "Fee Management", link: "/fee-management", icon: "clock-outline" },
    { module: "Chart currency", link: "/Chartcurrency", icon: "clock-outline" },
    { module: "Core wallet", link: "/corewalletbalance", icon: "clock-outline" },
    { module: "Referral Management", link: "", icon: "clock-outline" },
    { module: "History", link: "", icon: "clock-outline" },
];


const schema = yup.object().shape({
    status: yup.boolean(),
    permissions: yup.array().min(1, 'Select at least one permission').required('Permission is required'),
});

function SubAdmin() {
    const { handleSubmit, formState, control, setValue } = useForm({
        mode: 'onChange',
        resolver: yupResolver(schema),
    });
    const { errors } = formState;
    const [tempPage, setTempPage] = useState(0);
    const [tempRows, setTempRows] = useState(25);
    const [search, setSearch] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");
    const searchText = useDebounce(search);
    const [Dialog, confirmDelete] = useConfirm();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const toggle = () => setOpen(!open);
    const { id } = useParams();
    const toast = useToast();

    const { data: getSubAdmin, isLoading } = useSubAdminHistory({
        page: searchText ? 0 : tempPage,
        userId: id,
        size: tempRows,
        searchText,
        sortField: "_id",
        sortOrder: sortOrder
    });

    const totalRecords = getSubAdmin?.data?.totalRecords || 0;

    const {
        page,
        rowsPerPage,
        totalPages,
        handleChangePage,
        handleChangeRowsPerPage
    } = useBreadcrumbPagination(totalRecords, tempRows);

    useEffect(() => {
        setTempPage(page);
    }, [page]);

    useEffect(() => {
        setTempRows(rowsPerPage);
    }, [rowsPerPage]);

    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
        }
    });

    const deletesubmutation = useDeleteSub();
    const updateMutation = useUpdateSubAdmin();


    const [singleview, setSingleview] = useState('');
    const [selectedOptions, setSelectedOptions] = useState([]);

    const Edit = async (item) => {
        setSingleview(item);
        setSelectedOptions(item?.permission || []);
        setValue('status', item?.adminStatus);
        setValue('permissions', item?.permission || []);
        toggle();
    };

    const view = async (item) => {
        navigate(`/SubAdminHistory/${item._id}`, { state: { email: item.email } });
    };

    const deletesub = async (item) => {
        const isConfirmed = await confirmDelete();
        if (isConfirmed) {
            await deletesubmutation.mutateAsync({ _id: item._id });
        }
    };

    const checkAdminMutation = useCheckAdminAccess();
    const [access, setAccess] = useState(true);

    useEffect(() => {
        checkAdminAccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const checkAdminAccess = async () => {
        try {
            const obj = { page: 'SubAdmin' };
            const checkAdminRes = await checkAdminMutation.mutateAsync(obj);
            if (!checkAdminRes?.data?.status) {
                setAccess(false);
                logout();
            } else {
                setAccess(true);
            }
        } catch (error) {
            setAccess(false);
        }
    };

    const handleOptionChange = (option, checked) => {
        setSelectedOptions((prevSelectedOptions) => {
            let updatedSelectedOptions = [...prevSelectedOptions];
            const existingOptionIndex = updatedSelectedOptions.findIndex(
                (item) => item.title === option.module
            );

            if (existingOptionIndex !== -1) {
                if (!checked) {
                    updatedSelectedOptions.splice(existingOptionIndex, 1);
                }
            } else if (checked) {
                updatedSelectedOptions.push({
                    title: option.module,
                    icon: option.icon,
                    link: option.link,
                    status: checked,
                });
            }

            setValue('permissions', updatedSelectedOptions);
            if (updatedSelectedOptions.length > 0) {
                setValue('permissions', updatedSelectedOptions, { shouldValidate: true });
            }
            return updatedSelectedOptions;
        });
    };

    const arraysAreEqual = (arr1, arr2) => {
        if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
        return arr1.every((item, index) =>
            item.title === arr2[index].title &&
            item.icon === arr2[index].icon &&
            item.link === arr2[index].link &&
            item.status === arr2[index].status
        );
    };

    const onSubmit = async (data) => {
        if (selectedOptions.length < 1) {
            toast.error('Select at least one permission');
            return;
        }

        const statusChanged = singleview?.adminStatus !== data.status;
        const permissionsChanged = !arraysAreEqual(singleview?.permission, selectedOptions);

        if (statusChanged || permissionsChanged) {
            const obj = {
                _id: singleview?._id,
                adminStatus: data.status,
                permission: selectedOptions,
            };
            const res = await updateMutation.mutateAsync(obj);
            if (res.status) {
                toast.success("Status updated");
                queryClient.refetchQueries('sub-admin-History');
                setOpen(false);
            } else {
                toast.error(res.message);
                setOpen(false);
            }
        } else {
            toast.error("No changes done");
        }
    };

    const renderBlockIpTable = () => (
        getSubAdmin?.data?.subAdminList?.length > 0 ?
            <>

                {deletesubmutation?.isLoading &&
                    <PageLoader />
                }
                <div style={{ minHeight: "60vh" }}>
                    <div className='table-responsive'>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ maxWidth: "60px" }} align='center'>S.NO</TableCell>
                                    <TableCell align="center">USER NAME</TableCell>
                                    <TableCell align="center">EMAIL</TableCell>
                                    <TableCell align="center">Admin Status</TableCell>
                                    <TableCell align='center'>Action</TableCell>
                                    <TableCell align="center">Date and Time {sortOrder !== "desc" ? <ArrowUp onClick={() => setSortOrder('desc')} style={{ cursor: "pointer" }} /> : <ArrowDown onClick={() => setSortOrder('asc')} style={{ cursor: "pointer" }} />}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getSubAdmin?.data?.subAdminList?.map((row, i) => (
                                    <TableRow key={row.id || i}>
                                        <TableCell align='center'>{page * rowsPerPage + (i + 1)}</TableCell>
                                        <TableCell align="center">{row.name}</TableCell>
                                        <TableCell align="center">{row.email}</TableCell>
                                        <TableCell align="center">
                                            {row.adminStatus ?
                                                <>
                                                    <BadgeCheck className='mx-auto' color='green' size={15} />
                                                    <span className='text-green-600'>Active</span>
                                                </> :
                                                <>
                                                    <BadgeMinus className='mx-auto' color='red' size={15} />
                                                    <span className='text-red-600'>Disabled</span>
                                                </>
                                            }
                                        </TableCell>
                                        <TableCell align='center'>
                                            <div className='d-flex justify-content-center'>
                                                <UserRoundPen className="mx-3" size={20} onClick={() => Edit(row)} type='button' />
                                                <Trash className="mx-3" size={20} onClick={() => deletesub(row)} type='button' />
                                                <View className="mx-3" size={20} onClick={() => view(row)} type='button' />
                                            </div>
                                        </TableCell>
                                        <TableCell align="center">
                                            {moment(row.createdAt).format("DD-MM-YYYY | hh:mm A")}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                <BreadcrumbPagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </>
            :
            <NoRecords />
    );

    let content;

    if (isLoading) {
        content = <WaitingLoader />;
    } else if (access) {
        content = (
            <>
                <h2>Sub Admin</h2>
                <div className="ad_pg_bx bg-white p-4">
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                        <div className='col-md-4'>
                            <div className="input-group">
                                <span className="input-group-icon">
                                    <Search size={20} color='#aaa9a9' />
                                </span>
                                <input
                                    type="text"
                                    ref={inputRef}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="form-control"
                                    placeholder="Search email"
                                    value={search}
                                />
                            </div>
                        </div>
                        <button className='btn btn-primary d-flex align-items-center'
                            onClick={() => navigate('/Addsubadmin')}
                        >
                            <PlusCircle size={20} style={{ marginRight: "2px" }} /> Add
                        </button>
                    </div>
                    {isLoading ? <PageLoader /> : renderBlockIpTable()}
                </div>
                <Dialog />
                <Modal isOpen={open} toggle={toggle} centered>
                    <div className="modal-header">
                        <h1 className="modal-title fs-5">Update Sub-Admin</h1>
                        <button type="button" className="btn-close" onClick={toggle} aria-label="Close">
                            <img src={require('../../assets/images/icons/close-circle.svg').default} alt="close-circle" />
                        </button>
                    </div>
                    <ModalBody>
                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            <div className="col-sm-12 col-md-8 col-lg-8">
                                <div className="input-group mb-3">
                                    <span className="input-group-icon">
                                        <img src={user} className='img-fluid' alt="User" />
                                    </span>
                                    <Controller
                                        name='name'
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                defaultValue={singleview?.name}
                                                className="form-control"
                                                autoComplete='off'
                                                readOnly
                                            />
                                        )}
                                    />
                                </div>
                                <div className="input-group mb-3">
                                    <span className="input-group-icon">
                                        <img src={user} className='img-fluid' alt="User" />
                                    </span>
                                    <Controller name='email' control={control} render={({ field }) => (
                                        <input
                                            {...field}
                                            className="form-control"
                                            autoComplete='off'
                                            readOnly
                                            defaultValue={singleview?.email}
                                        />
                                    )} />
                                </div>
                                <div>
                                    <label htmlFor="login-status">Staff Login Status: </label>
                                    <span id="login-status" aria-live="polite">
                                        {singleview?.adminStatus ? (
                                            <span style={{ color: 'green' }}>Enabled</span>
                                        ) : (
                                            <span style={{ color: 'red' }}>Disabled</span>
                                        )}
                                    </span>
                                </div>
                                <Controller
                                    name='status'
                                    control={control}
                                    defaultValue={singleview?.adminStatus}
                                    render={({ field }) => (
                                        <>
                                            <label
                                                htmlFor="status-checkbox"
                                                style={{ display: 'flex', alignItems: 'center' }}
                                            >
                                                <span>Status: </span>
                                                <input
                                                    {...field}
                                                    id="status-checkbox"
                                                    type='checkbox'
                                                    checked={field.value}
                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                    style={{
                                                        margin: "4px 10px 0 10px",
                                                        accentColor: 'blue'
                                                    }}
                                                />
                                            </label>
                                            {errors.status && (
                                                <div style={{ color: 'red', fontSize: '12px' }}>{errors.status.message}</div>
                                            )}
                                        </>
                                    )}
                                />
                                <div>
                                    <span style={{ fontWeight: 'bolder', marginTop: '4px' }}>Modules:</span>
                                    <div className='mt-3'>
                                        {allOptions.map((option) => {
                                            const isChecked = selectedOptions.some(
                                                (perm) => perm.title === option.module
                                            );

                                            return (
                                                <div key={option.module} className='mb-8 d-flex align-items-center'>
                                                    <input
                                                        type="checkbox"
                                                        id={`module-${option.module}`} // Unique ID for each option
                                                        name={option.module}
                                                        checked={!!isChecked}
                                                        onChange={(e) => handleOptionChange(option, e.target.checked)}
                                                        style={{ marginRight: '10px' }}
                                                    />
                                                    <label htmlFor={`module-${option.module}`} style={{ fontWeight: 'bold' }}>
                                                        {option.module}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                        {errors.permissions && (
                                            <div style={{ color: 'red', fontSize: '12px' }}>
                                                {errors.permissions.message}
                                            </div>
                                        )}
                                    </div>
                                </div>


                            </div>
                            <div className="d-grid crPgbtn pt-3">
                                <button type="submit" className="btn btn-primary">
                                    Submit
                                </button>
                            </div>
                        </form>
                    </ModalBody>
                </Modal>
            </>
        );
    } else {
        content = <PermissionDenied />;
    }

    return <div>{content}</div>;

}

export default SubAdmin;